import { useEffect, useState } from 'react'
import { Badge, Button, Modal, TextInput } from '../../components/forms'
import { describeError } from '../../lib/forms'
import { formatMoneyDocument, formatTime } from '../../lib/format'
import { findClaimable, useMpesaRequest, useMpesaStatus, useRequestMpesa, useWaitingPayments, type MpesaPaymentItem } from '../../features/payments/api'

export type MpesaTaken = {
  id: string
  code: string
  amount: number
  /** Whose number paid, once Safaricom has said so (D-202). Never set for a till payment. */
  payer?: { id: string; name: string; wasCreated: boolean }
}

/**
 * An M-Pesa tender at the till (D-082, D-259, D-261).
 *
 * Settled, this is a badge. Unsettled, it is a button that opens the payment window, because a
 * till with three ways to take money needs them laid out, not crammed into the width of a
 * tender row.
 */
export function MpesaTender({
  locationId,
  amount,
  phone,
  paidCode,
  onPaid,
  onClear,
}: {
  locationId: string
  amount: number
  phone?: string
  paidCode?: string
  onPaid: (payment: MpesaTaken) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)

  if (paidCode) {
    return (
      <span className="flex items-center gap-2">
        <Badge tone="success">M-Pesa {paidCode}</Badge>
        <Button tone="ghost" aria-label="Clear the M-Pesa payment" onClick={onClear}>Change</Button>
      </span>
    )
  }

  return (
    <>
      <Button tone="secondary" disabled={Math.ceil(amount) < 1} onClick={() => setOpen(true)}>
        Take M-Pesa payment{amount >= 1 ? ` · ${formatMoneyDocument(Math.ceil(amount))}` : ''}
      </Button>
      {open && (
        <PaymentWindow
          locationId={locationId}
          amount={amount}
          phone={phone}
          onClose={() => setOpen(false)}
          onPaid={(p) => { setOpen(false); onPaid(p) }}
        />
      )}
    </>
  )
}

/**
 * The payment window: three ways in, each with its own button (D-261).
 *
 * <b>Send a prompt</b> rings the customer's phone. It is first because it is the only one that
 * comes back with a number: Safaricom gives the full MSISDN on an STK result, so the payer becomes
 * a customer record by itself and the thank-you message has somewhere to go.
 *
 * <b>Wait for the till</b> watches for money paid straight to the till number. It starts when the
 * cashier presses it rather than the moment the window opens, because the question "has it come
 * in yet" is one a person asks at a particular moment, and a till polling on the off-chance is a
 * query every two seconds in every lane all day.
 *
 * <b>Type the code</b> is the fallback for a payment that arrived before anyone was looking.
 *
 * On a till payment Safaricom sends a <em>hashed</em> number and a first name — so there is a name
 * to greet and nothing to dial. The window says so rather than letting the cashier assume the
 * customer was captured.
 */
function PaymentWindow({ locationId, amount, phone: suggestedPhone, onPaid, onClose }: {
  locationId: string
  amount: number
  phone?: string
  onPaid: (payment: MpesaTaken) => void
  onClose: () => void
}) {
  const status = useMpesaStatus()
  const request = useRequestMpesa()
  const [phone, setPhone] = useState(suggestedPhone ?? '')
  const [requestId, setRequestId] = useState<string | null>(null)
  const [watching, setWatching] = useState(false)
  const [code, setCode] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [finding, setFinding] = useState(false)
  const [tookFromTill, setTookFromTill] = useState<MpesaPaymentItem | null>(null)

  const state = useMpesaRequest(requestId)
  const r = state.data
  const whole = Math.ceil(amount)
  const waiting = request.isPending || r?.status === 'Pending' || (requestId !== null && !r)
  const arrivals = useWaitingPayments(locationId, watching).data ?? []

  useEffect(() => {
    if (r?.status === 'Paid' && r.mpesaPaymentId && r.transactionCode) {
      onPaid({
        id: r.mpesaPaymentId,
        code: r.transactionCode,
        amount: r.amount,
        // The number that paid now belongs to somebody: a record this payment created, or an
        // account that was already on file. The till decides what to do with each (D-202).
        payer: r.payerCustomerId && r.payerCustomerName
          ? { id: r.payerCustomerId, name: r.payerCustomerName, wasCreated: r.payerCustomerWasCreated === true }
          : undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once, when it is paid
  }, [r?.status])

  return (
    <Modal open wide title={`Take M-Pesa payment · ${formatMoneyDocument(whole)}`} onClose={onClose}
      footer={<Button tone="secondary" onClick={onClose}>Close</Button>}>
      {tookFromTill ? (
        <TillPaymentTaken payment={tookFromTill} onDone={() => onPaid({ id: tookFromTill.id, code: tookFromTill.transactionCode, amount: tookFromTill.amount })} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-md border border-dpl-border p-3">
            <h3 className="text-sm font-semibold">Send a prompt to their phone</h3>
            <p className="mb-2 text-xs text-dpl-muted">
              The only way that comes back with the customer’s number — they are saved and sent the thank-you by themselves.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <TextInput aria-label="M-Pesa phone" inputMode="tel" placeholder="07… phone" className="w-40" value={phone} disabled={waiting} onChange={(e) => setPhone(e.target.value)} />
              <Button busy={waiting} disabled={whole < 1 || !phone.trim()}
                onClick={() => {
                  setProblem(null)
                  request.mutate({ locationId, purpose: 'Sale', phone, amount: whole }, { onSuccess: (x) => setRequestId(x.id), onError: (e) => setProblem(describeError(e)) })
                }}>
                Send prompt for {formatMoneyDocument(whole)}
              </Button>
            </div>
            {waiting && requestId && <p className="mt-2 text-xs text-dpl-muted" role="status">Waiting for the customer to enter their M-Pesa PIN…</p>}
            {r && r.status !== 'Pending' && r.status !== 'Paid' && <p className="mt-2 text-xs text-dpl-danger" role="alert">Not paid: {r.resultDescription ?? r.status}. Try again or take it another way.</p>}
          </section>

          <section className="rounded-md border border-dpl-border p-3">
            <h3 className="text-sm font-semibold">Or wait for the till</h3>
            <p className="mb-2 text-xs text-dpl-muted">
              For a customer who pays the till number themselves. Safaricom sends their name but not their number.
            </p>
            {watching ? (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs text-dpl-muted" role="status">Watching the till…</span>
                  <Button tone="ghost" onClick={() => setWatching(false)}>Stop</Button>
                </div>
                <Arrivals rows={arrivals} expected={whole} onTake={setTookFromTill} />
              </>
            ) : (
              <Button tone="secondary" onClick={() => setWatching(true)}>Wait for the till</Button>
            )}
          </section>

          <section className="rounded-md border border-dpl-border p-3">
            <h3 className="text-sm font-semibold">Or type the code</h3>
            <p className="mb-2 text-xs text-dpl-muted">For a payment that came in before anyone was looking.</p>
            <div className="flex flex-wrap items-center gap-2">
              <TextInput aria-label="Paybill code" placeholder="M-Pesa code" className="w-40 uppercase" value={code} onChange={(e) => setCode(e.target.value)} />
              <Button tone="secondary" busy={finding} disabled={code.trim().length < 6}
                onClick={async () => {
                  setProblem(null)
                  setFinding(true)
                  try {
                    setTookFromTill(await findClaimable(code.trim(), locationId))
                  } catch (e) {
                    setProblem(describeError(e))
                  } finally {
                    setFinding(false)
                  }
                }}>
                Find it
              </Button>
            </div>
          </section>

          {problem && <p className="text-xs text-dpl-danger" role="alert">{problem}</p>}
          {status.data?.isSandbox && <p className="text-xs text-[#8A5A00]">Sandbox — no real money. A phone ending 0000 declines.</p>}
        </div>
      )}
    </Modal>
  )
}

/**
 * What the cashier is told after taking money off the till.
 *
 * The name is all Safaricom discloses — the number arrives hashed — so the window says that
 * plainly and points at the one thing that would fix it. A cashier who assumes the customer was
 * captured is a cashier who never captures one.
 */
function TillPaymentTaken({ payment, onDone }: { payment: MpesaPaymentItem; onDone: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm">
        Taking <span className="font-medium">{formatMoneyDocument(payment.amount)}</span> from{' '}
        <span className="font-medium">{payment.payerName ?? 'the till'}</span> · {payment.transactionCode}
      </p>
      <p className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2 text-xs text-dpl-muted">
        Safaricom gives the name on a till payment and not the number, so nothing can be saved or
        texted from this on its own. To keep {payment.payerName ?? 'them'} as a customer and send the
        thank-you, put their number in the customer box on the sale before you finish it.
      </p>
      <Button onClick={onDone}>Use this payment</Button>
    </div>
  )
}

/**
 * What has come into the till and nobody has used, as it lands.
 *
 * The one matching the sale is put first and marked, because at a busy counter the cashier is
 * looking for a figure, not reading a list. The rest are still shown: a customer who paid a round
 * 2,000 for a 1,840 sale has paid, and hiding it because the arithmetic differs sends the cashier
 * back to asking for the code.
 *
 * Nothing is ever taken automatically. Two customers paying the same round figure a minute apart
 * is an ordinary afternoon, and attaching the wrong one takes a stranger's money for this sale and
 * leaves theirs unapplied, with the screen saying paid.
 */
function Arrivals({ rows, expected, onTake }: {
  rows: MpesaPaymentItem[]
  expected: number
  onTake: (payment: MpesaPaymentItem) => void
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-dpl-muted">Nothing yet — a payment made to the till number shows here as it arrives.</p>
  }

  const matches = (p: MpesaPaymentItem) => Math.round(p.amount) === expected
  const sorted = [...rows].sort((a, b) => Number(matches(b)) - Number(matches(a)))

  return (
    <ul className="space-y-1" aria-label="Payments waiting at this till">
      {sorted.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => onTake(p)}
            className={`flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-sm hover:border-dpl-primary ${
              matches(p) ? 'border-dpl-success bg-dpl-success/5' : 'border-dpl-border bg-dpl-surface'
            }`}
          >
            <span className="min-w-0">
              <span className="font-medium tabular-nums">{formatMoneyDocument(p.amount)}</span>
              {matches(p) && <span className="ml-2 text-xs font-medium text-dpl-success">matches this sale</span>}
              <span className="block truncate text-[11px] text-dpl-muted">
                {[p.payerName, p.transactionCode].filter(Boolean).join(' · ')} · {formatTime(p.receivedAt)}
              </span>
            </span>
            <span className="shrink-0 text-xs font-medium text-dpl-primary">Use this</span>
          </button>
        </li>
      ))}
    </ul>
  )
}
